import {
  Crosshair,
  Mountain,
  LandPlot,
  FileText,
  Building2,
  Map,
  Database,
  Home,
  LayoutGrid,
  ScrollText,
  Construction,
  Cable,
  Route,
  Plane,
  FileWarning,
  BookOpen,
  Layers,
  Milestone,
  FileDigit,
  ScanLine,
  Cpu,
} from "lucide-react";

const img = (file) => `https://static.prod-images.emergentagent.com/jobs/7631f423-53c6-4462-98ac-35ec74ffec2d/images/${file}`;

export const IMG = {
  showcase_topo: img("f176c683441fa58499f16b6ac82acf621ea6f82debdb73279ec7a9949834f64a.jpeg"),
  showcase_boundary: img("9150611157ed7a3df999faed657e9fa88b778c538fe09d5c30c1e58a3a9ba6cc.jpeg"),
  showcase_contour: img("242b595ce7c7a5c44cd48f01e8718cfa5c0c14061d425fcbef49afac322efecf.jpeg"),
  showcase_asbuilt: img("ee6d44c8b03ab03f33b3fccc518480d47177bc6d85024937a2ff9ccef85d8292.jpeg"),
  showcase_gis_parcel: img("eb394d935d9849dd3a11f1000bccb3a487cce60512e3f71803ffc3cecc56e32e.jpeg"),
  showcase_conversion: img("ab473cc74bb9c0baec7a49f9d0a46ecd612a0746b5f2a224b1c86e1b42d11193.jpeg"),
  gis_layers: img("d8aeca770683c67fa1409676c71421cea5dfd1289af2a0f4e033c95e3bcb6174.jpeg"),
  workstation: img("238f98b6f211971251394375694e4dff4a7b96a79cfb592a90eff0748c05b0ef.jpeg"),
  field_surveyor:
    "https://images.unsplash.com/photo-1766830110938-0ea8a6d78ecb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  construction_survey:
    "https://images.pexels.com/photos/5802827/pexels-photo-5802827.jpeg?auto=compress&cs=tinysrgb&w=1200",
  gnss_receiver:
    "https://images.unsplash.com/photo-1778041582108-2e765e738c8a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  aerial_river:
    "https://images.pexels.com/photos/15509902/pexels-photo-15509902.jpeg?auto=compress&cs=tinysrgb&w=1200",
  terrain_contours:
    "https://images.unsplash.com/photo-1730314737937-e8322dddfc56?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

export const SERVICES = [
  {
    slug: "land-survey-cad-drafting",
    name: "Land Survey CAD Drafting",
    navLabel: "Land Survey CAD Drafting",
    icon: Crosshair,
    short:
      "Preparation of structured CAD drawings from supplied survey data, field observations, coordinate files, sketches, and project instructions.",
    metaTitle: "Land Survey CAD Drafting Services | GeoDrafts",
    metaDescription:
      "Outsourced land survey CAD drafting from supplied coordinates, sketches and instructions — structured, layer-organized DWG drawings prepared to your drafting standards.",
    headline: "Survey CAD Drafting Built on Your Standards",
    intro:
      "GeoDrafts turns supplied survey data — coordinate files, field sketches, point lists, and project instructions — into structured, layer-organized CAD drawings that are ready for review, issue, and client handover.",
    image: IMG.showcase_topo,
    imageAlt: "Illustrative topographic CAD survey plan sample with contour linework",
    scope:
      "We prepare base sheets, import and organize supplied points, draft plan features, apply your layer structure and naming conventions, complete annotation and title blocks, and assemble sheet sets for review. Every drawing is prepared against a defined checklist so the output matches the instructions agreed at the start of the project.",
    inputs: [
      "Coordinate and point files (CSV, TXT, PNEZD-style lists)",
      "Field sketches, measurement notes, and PDF markups",
      "Existing DWG/DXF drawings and reference material",
      "Client CAD templates, layer standards, and plot styles",
      "Written project instructions and drafting standards",
    ],
    deliverables: [
      "Layer-organized plan drawings",
      "Sheet sets with title blocks and legends",
      "Annotation, dimensions, and callouts as instructed",
      "PDF plots alongside editable CAD files",
    ],
    formats: ["DWG", "DXF", "PDF", "DGN on request"],
    applications: [
      "Project documentation and record drawings",
      "Client and contractor communication sets",
      "Permit submission packages prepared by the client",
      "Archiving consistent with your file structure",
    ],
    workflow: [
      { title: "Review inputs", desc: "Points, sketches, and instructions are checked for completeness." },
      { title: "Set up template", desc: "Your template, layers, and styles are applied before drafting starts." },
      { title: "Draft and annotate", desc: "Plan features, linework, and annotation are completed to scope." },
      { title: "Quality review", desc: "Drawings are checked against the project checklist." },
      { title: "Deliver files", desc: "Final files are handed over in the agreed folder structure." },
    ],
    faqs: [
      {
        q: "Do you work with our existing CAD standards?",
        a: "Yes. Layer structures, naming conventions, title blocks, and plot styles you supply are applied to the deliverables as agreed.",
      },
      {
        q: "What do you need to start a drawing?",
        a: "At minimum: the survey data you want drawn, any reference drawings, your template or standards, and written instructions describing the required output.",
      },
      {
        q: "Can you match a previous drawing's presentation style?",
        a: "Yes. Share a sample sheet and we will follow its layout, symbols, and annotation approach as closely as the supplied data allows.",
      },
    ],
    related: ["topographic-survey-drafting", "as-built-survey-drafting", "alta-nsps-survey-drafting"],
    note: "GeoDrafts provides drafting production only. Final review, approval, and professional certification remain with the licensed surveyor responsible for the project.",
  },
  {
    slug: "topographic-survey-drafting",
    name: "Topographic Survey Drafting",
    navLabel: "Topographic Survey Drafting",
    icon: Mountain,
    short:
      "Drafting of terrain and site information, including supplied spot levels, contours, breaklines, natural features, and constructed elements, as appropriate to the project scope.",
    metaTitle: "Topographic Survey Drafting Services | GeoDrafts",
    metaDescription:
      "Topographic survey drawing services from supplied spot levels, contour data and breaklines — clean, readable terrain and site plans prepared to your standards.",
    headline: "Terrain, Drawn Clean and Readable",
    intro:
      "GeoDrafts prepares topographic survey drawings from the terrain data you supply — organizing spot elevations, contours, breaklines, natural features, and constructed elements into a clear, consistent site plan.",
    image: IMG.showcase_contour,
    imageAlt: "Illustrative contour and elevation map sample",
    scope:
      "We structure supplied elevation data into readable contour layers, place spot levels and breaklines as instructed, draft natural and constructed features from your observations, and present everything with consistent symbols and a clear surface-reading hierarchy across the sheet set.",
    inputs: [
      "Spot level and elevation data supplied by the client",
      "Contour and breakline data (from field or photogrammetric sources)",
      "Feature observations and sketches",
      "Aerial or drone-derived surface files, where provided",
      "Client symbology, layering, and sheet standards",
    ],
    deliverables: [
      "Topographic plan drawings with organized contour layers",
      "Spot elevation blocks placed per instructions",
      "Feature linework for natural and constructed elements",
      "PDF plots and editable CAD files",
    ],
    formats: ["DWG", "DXF", "PDF", "LandXML points on request"],
    applications: [
      "Site design support packages for engineers",
      "Planning and permit documentation prepared by the client",
      "Volume and surface reference drawings as instructed",
      "Baseline plans for development projects",
    ],
    workflow: [
      { title: "Inspect terrain data", desc: "Supplied elevations, contours, and breaklines are reviewed." },
      { title: "Build the base plan", desc: "Surfaces and levels are organized into the agreed layer structure." },
      { title: "Draft features", desc: "Natural and constructed features are drawn from supplied observations." },
      { title: "Review against instructions", desc: "The sheet set is checked for completeness and readability." },
      { title: "Deliver files", desc: "Final drawings are delivered in the agreed formats." },
    ],
    faqs: [
      {
        q: "Do you generate survey data or fly drones?",
        a: "No. We draft from the data you supply — field observations, photogrammetric outputs, or existing surface files.",
      },
      {
        q: "Can you draft from point cloud or drone processing outputs?",
        a: "Yes, where such files are supplied, we can draft contours and features from them according to your instructions.",
      },
      {
        q: "How are contours handled?",
        a: "Contour data supplied by the client is organized into dedicated layers with the interval and presentation you specify.",
      },
    ],
    related: ["land-survey-cad-drafting", "gis-mapping-services", "boundary-survey-drafting"],
    note: "Terrain data is drafted as supplied. Surface interpretation, data capture, and professional verification remain with the responsible surveyor or engineer.",
  },
  {
    slug: "boundary-survey-drafting",
    name: "Boundary and Property Survey Drafting",
    navLabel: "Boundary and Property Survey Drafting",
    icon: LandPlot,
    short:
      "Preparation of boundary and property survey drawing deliverables based on client-provided survey information, records, measurements, and applicable drafting instructions.",
    metaTitle: "Boundary Survey CAD Drafting Services | GeoDrafts",
    metaDescription:
      "Boundary and property survey drafting from client-supplied measurements, records and instructions — parcel plans, monument symbols and area tables, drafted to your standards.",
    headline: "Boundary Plans Prepared With Care",
    intro:
      "GeoDrafts drafts boundary and property survey deliverables from the measurements, records, and instructions supplied by the responsible surveyor — presenting parcels, monumentation, and references exactly as provided.",
    image: IMG.showcase_boundary,
    imageAlt: "Illustrative property boundary plan sample with parcel linework",
    scope:
      "We draft parcel linework from supplied bearings, distances, and coordinates, place monument and corner symbols per your legend, prepare area and listing tables as instructed, and assemble the sheets so the drawing reads consistently with the records you provide.",
    inputs: [
      "Bearing, distance, and coordinate data supplied by the client",
      "Deed descriptions and records provided for reference",
      "Monumentation notes and corner information",
      "Prior plats and survey drawings, where supplied",
      "Client symbology, tables, and sheet standards",
    ],
    deliverables: [
      "Boundary plan drawings with parcel linework",
      "Corner and monument symbols per the client legend",
      "Area, bearing, and distance tables as instructed",
      "PDF plots and editable CAD files",
    ],
    formats: ["DWG", "DXF", "PDF"],
    applications: [
      "Title work support packages for surveying firms",
      "Record plans and client report attachments",
      "Property presentation sets for stakeholders",
      "Consistent archiving of boundary documentation",
    ],
    workflow: [
      { title: "Review supplied records", desc: "Measurements, coordinates, and reference records are checked." },
      { title: "Draft parcel linework", desc: "Parcels are drawn exactly from the supplied data." },
      { title: "Apply symbols and tables", desc: "Monuments, corners, and tables are placed per your legend." },
      { title: "Review against instructions", desc: "The drawing is checked line-by-line against the source data." },
      { title: "Deliver files", desc: "Final drawings are delivered in the agreed structure." },
    ],
    faqs: [
      {
        q: "Does GeoDrafts determine legal boundaries?",
        a: "No. Boundary determination is a licensed professional's responsibility. We draft plans from the measurements, records, and instructions the responsible surveyor supplies.",
      },
      {
        q: "Can you follow a specific plat presentation style?",
        a: "Yes. Share your plat standards or a reference sheet and we will follow the agreed presentation.",
      },
      {
        q: "What if supplied records conflict?",
        a: "We flag the conflict and draft according to your written direction — decisions about boundary evidence remain with the responsible surveyor.",
      },
    ],
    related: ["alta-nsps-survey-drafting", "land-survey-cad-drafting", "topographic-survey-drafting"],
    note: "GeoDrafts does not determine, certify, or verify legal boundaries. All boundary determinations and professional sign-off remain with the licensed surveyor responsible for the project.",
  },
  {
    slug: "alta-nsps-survey-drafting",
    name: "ALTA/NSPS Survey Drafting",
    navLabel: "ALTA/NSPS Survey Drafting",
    icon: FileText,
    short:
      "CAD drafting support for ALTA/NSPS Land Title Surveys using client-supplied survey information and applicable project requirements.",
    metaTitle: "ALTA/NSPS Survey Drafting Support | GeoDrafts",
    metaDescription:
      "ALTA/NSPS land title survey drafting support for licensed surveying firms — plan preparation, Table A item annotation as directed, and exhibits from your supplied data.",
    headline: "ALTA/NSPS Drafting Support, Sheet by Sheet",
    intro:
      "GeoDrafts provides CAD drafting support for ALTA/NSPS Land Title Surveys, working from client-supplied survey information and the project requirements you direct — under the review and certification of the licensed surveyor.",
    image: IMG.showcase_topo,
    imageAlt: "Illustrative ALTA-style survey plan linework sample",
    scope:
      "We prepare ALTA-style plan drawings on your templates, annotate the items you instruct us to show, draft exhibits and detail sheets, and keep the drawing set consistent with the checklist structure your firm uses for land title survey production.",
    inputs: [
      "Field data, measurements, and observations supplied by the client",
      "Prior surveys and record documents provided for reference",
      "Table A item direction provided by the client",
      "Client ALTA templates, symbols, and sheet standards",
      "Written instructions for exhibits and detail sheets",
    ],
    deliverables: [
      "ALTA-style plan drawings on client templates",
      "Table A item annotation as directed by the client",
      "Exhibit and detail sheets as instructed",
      "PDF plots and editable CAD files",
    ],
    formats: ["DWG", "DXF", "PDF"],
    applications: [
      "Land title survey production support for surveying firms",
      "Consistent multi-site survey drawing sets",
      "Exhibit preparation for client packages",
      "Drawing archives maintained to your standard",
    ],
    workflow: [
      { title: "Review project direction", desc: "Supplied data and instructed items are mapped to a drafting plan." },
      { title: "Set up the sheet", desc: "Your ALTA template, symbols, and layout are applied." },
      { title: "Draft and annotate", desc: "Plan linework and instructed items are completed." },
      { title: "Check against direction", desc: "The drawing is reviewed against the client's checklist." },
      { title: "Deliver files", desc: "Final drawings are delivered for professional review." },
    ],
    faqs: [
      {
        q: "Does GeoDrafts certify ALTA/NSPS surveys?",
        a: "No. Certification, professional review, and compliance sign-off belong to the licensed surveyor. We provide drafting production support only.",
      },
      {
        q: "Do you decide which Table A items apply?",
        a: "No. The client directs which items are to be shown; we draft exactly per that written direction.",
      },
      {
        q: "Can you support multiple sites at once?",
        a: "Yes, subject to capacity. Multi-site sets are organized with consistent templates and a shared checklist.",
      },
    ],
    related: ["boundary-survey-drafting", "land-survey-cad-drafting", "as-built-survey-drafting"],
    note: "GeoDrafts is not a licensed surveying firm and does not provide professional certification or independently verify legal compliance. All surveys are certified by the responsible licensed surveyor.",
  },
  {
    slug: "as-built-survey-drafting",
    name: "As-Built and Existing Conditions Drafting",
    navLabel: "As-Built and Existing Conditions Drafting",
    icon: Building2,
    short:
      "Conversion of supplied field measurements and site observations into organized drawings showing documented existing conditions.",
    metaTitle: "As-Built & Existing Conditions Drafting | GeoDrafts",
    metaDescription:
      "As-built and existing conditions drafting from supplied field measurements and observations — organized site and facility drawings prepared to your standards.",
    headline: "Existing Conditions, Clearly Documented",
    intro:
      "GeoDrafts converts supplied field measurements and site observations into organized drawings that document existing conditions — buildings, site elements, and observed utilities — in a consistent, readable format.",
    image: IMG.showcase_asbuilt,
    imageAlt: "Illustrative existing conditions site plan sample",
    scope:
      "We translate supplied measurements and observation records into dimensioned drawings, apply consistent symbols for observed elements, layer utilities and site features separately, and format the sheet set so downstream teams can read the documented conditions at a glance.",
    inputs: [
      "Field measurements, notes, and sketches supplied by the client",
      "Observation records and photographs, where provided",
      "Prior drawings and reference plans, where supplied",
      "Client symbols, layers, and sheet standards",
      "Written instructions on required detail level",
    ],
    deliverables: [
      "As-built and existing conditions plan drawings",
      "Dimensioned layouts of documented elements",
      "Layered utility and site feature linework as observed",
      "PDF plots and editable CAD files",
    ],
    formats: ["DWG", "DXF", "PDF"],
    applications: [
      "Renovation and fit-out documentation support",
      "Facility and site record drawings",
      "Permit packages prepared by the client",
      "Handover documentation sets",
    ],
    workflow: [
      { title: "Review field records", desc: "Supplied measurements and observations are organized." },
      { title: "Build the plan", desc: "Documented conditions are drafted into the agreed layer structure." },
      { title: "Dimension and symbolize", desc: "Elements are annotated per your standards." },
      { title: "Quality review", desc: "Drawings are checked against the supplied records." },
      { title: "Deliver files", desc: "Final drawings are delivered in the agreed formats." },
    ],
    faqs: [
      {
        q: "Do you verify measurements on site?",
        a: "No. All measurements and observations are supplied by the client; we organize and draft them.",
      },
      {
        q: "Can you show utilities?",
        a: "Yes, as observed and supplied — utility linework is drafted from your records and clearly presented as documented conditions.",
      },
      {
        q: "Can drawings follow a specific discipline's style?",
        a: "Yes. Share your standard sheets and we will match the agreed presentation.",
      },
    ],
    related: ["land-survey-cad-drafting", "topographic-survey-drafting", "gis-data-conversion"],
    note: "Existing conditions are drafted as documented in supplied records. Field verification and professional responsibility remain with the client and their licensed surveyor.",
  },
  {
    slug: "gis-mapping-services",
    name: "GIS Mapping and Spatial Data",
    navLabel: "GIS Mapping and Spatial Data",
    icon: Map,
    short:
      "Creation and organization of GIS layers, spatial datasets, feature attributes, map layouts, and geospatial deliverables according to client specifications.",
    metaTitle: "GIS Mapping Services | GeoDrafts",
    metaDescription:
      "GIS mapping and spatial data services — layer organization, thematic map production, attribute structuring and cartographic layouts in Shapefile, GeoPackage, GeoJSON and KML formats.",
    headline: "Turn Spatial Data into Useful Information",
    intro:
      "GeoDrafts organizes geospatial information into practical datasets and clear visual deliverables — structured layers, consistent attributes, and cartographic layouts prepared to your specifications.",
    image: IMG.showcase_gis_parcel,
    imageAlt: "Illustrative GIS parcel map sample with layered spatial data",
    scope:
      "We create and organize GIS layers from your source data, structure feature attributes into usable fields, produce thematic and reference map layouts, and handle coordinate systems and projections according to the specifications you supply.",
    inputs: [
      "Shapefiles, GeoPackage, GeoJSON, KML/KMZ, and CSV data",
      "CAD drawings for spatial reference, where supplied",
      "Spreadsheets and attribute tables to be joined",
      "Imagery or basemaps provided by the client",
      "Written specifications for layers, attributes, and CRS",
    ],
    deliverables: [
      "Organized GIS layers with structured attributes",
      "Thematic and reference map layouts",
      "Feature editing and layer organization",
      "Data dictionaries describing delivered datasets",
    ],
    formats: ["Shapefile", "GeoPackage", "GeoJSON", "KML/KMZ", "CSV", "PDF map layouts"],
    applications: [
      "Planning and asset presentation maps",
      "Parcel and land-base mapping from supplied records",
      "Thematic maps for reports and stakeholders",
      "Spatial datasets prepared for team workflows",
    ],
    workflow: [
      { title: "Review source data", desc: "Supplied datasets, attributes, and specifications are inspected." },
      { title: "Structure the layers", desc: "Features and attributes are organized to the agreed schema." },
      { title: "Produce map layouts", desc: "Cartographic outputs are styled per your direction." },
      { title: "Verify against specs", desc: "Layers, attributes, and CRS are checked to the specification." },
      { title: "Deliver datasets", desc: "Files are delivered with an organized, documented structure." },
    ],
    faqs: [
      {
        q: "Which GIS formats do you deliver?",
        a: "Shapefile, GeoPackage, GeoJSON, KML/KMZ, and CSV as standard; other agreed formats by arrangement.",
      },
      {
        q: "Can you follow our layer naming schema?",
        a: "Yes. Deliverables follow the layer naming, attribute fields, and CRS you specify.",
      },
      {
        q: "Do you produce print-ready map layouts?",
        a: "Yes — thematic and reference layouts can be delivered as PDF exports at the size and scale you need.",
      },
    ],
    related: ["gis-data-conversion", "land-survey-cad-drafting", "topographic-survey-drafting"],
  },
  {
    slug: "gis-data-conversion",
    name: "GIS Data Conversion and Digitization",
    navLabel: "GIS Data Conversion and Digitization",
    icon: Database,
    short:
      "CAD-to-GIS and GIS-to-CAD conversion, spatial data digitization, geospatial data cleanup, and attribute organization according to supplied specifications.",
    metaTitle: "GIS Data Conversion & CAD-to-GIS Services | GeoDrafts",
    metaDescription:
      "CAD to GIS conversion, digitization and spatial data cleanup — structured Shapefile, GeoPackage and GeoJSON datasets from supplied CAD drawings, maps and scans.",
    headline: "CAD-to-GIS Conversion Without the Cleanup Headache",
    intro:
      "GeoDrafts converts supplied CAD drawings, maps, and scans into structured GIS datasets — and moves GIS data back into CAD deliverables — with geometry cleanup and attribute organization handled throughout.",
    image: IMG.showcase_conversion,
    imageAlt: "Illustrative CAD-to-GIS conversion workflow sample",
    scope:
      "We convert CAD linework into structured GIS features, digitize from supplied maps and imagery, clean and organize geometry, structure attributes into usable fields, and manage coordinate system and projection handling according to the specifications you provide.",
    inputs: [
      "CAD drawings (DWG/DXF) with or without attributes",
      "Scanned maps, PDFs, and imagery for digitization",
      "Existing GIS datasets for cleanup or conversion",
      "Coordinate system and projection specifications",
      "Target schema, layer naming, and attribute requirements",
    ],
    deliverables: [
      "Structured GIS datasets from CAD or scanned sources",
      "Cleaned geometry with organized attributes",
      "CAD exports from GIS data as required",
      "Conversion notes documenting assumptions",
    ],
    formats: ["Shapefile", "GeoPackage", "GeoJSON", "KML/KMZ", "DWG", "DXF", "CSV"],
    applications: [
      "Migrating drawing records into GIS workflows",
      "Digitizing legacy paper and PDF maps",
      "Sharing spatial data across CAD and GIS teams",
      "Preparing datasets for mapping platforms",
    ],
    workflow: [
      { title: "Inspect sources", desc: "CAD files, scans, and target specs are reviewed together." },
      { title: "Define the schema", desc: "Layer structure, fields, and CRS are confirmed with you." },
      { title: "Convert and clean", desc: "Geometry is converted, cleaned, and attributed." },
      { title: "Verify output", desc: "Converted datasets are checked against the target schema." },
      { title: "Deliver datasets", desc: "Files are handed over with conversion notes." },
    ],
    faqs: [
      {
        q: "Can you preserve CAD attributes during conversion?",
        a: "Yes, where attributes exist in the supplied files they are mapped into the target schema you define.",
      },
      {
        q: "How do you handle coordinate systems?",
        a: "According to your written specification — source and target CRS are agreed before conversion starts.",
      },
      {
        q: "Do you digitize from paper maps?",
        a: "Yes, from supplied scans or imagery, at the accuracy expectations agreed in the project scope.",
      },
    ],
    related: ["gis-mapping-services", "land-survey-cad-drafting", "as-built-survey-drafting"],
  },
  {
    slug: "mortgage-location-survey-drafting",
    name: "Mortgage Location Survey Drafting",
    navLabel: "Mortgage Location Survey Drafting",
    icon: Home,
    short:
      "Compact location drawings showing improvements relative to property lines, drafted from supplied field and record data.",
    metaTitle: "Mortgage Location Survey Drafting | GeoDrafts",
    metaDescription:
      "Mortgage location survey drafting from supplied field measurements and records — improvement locations, ties and property relationships on compact, readable sheets.",
    headline: "Location Drawings Lenders Can Read at a Glance",
    intro:
      "GeoDrafts prepares mortgage location survey drawings from the field measurements and record information you supply — improvements, ties, and property relationships presented clearly on a compact sheet.",
    image: IMG.showcase_boundary,
    imageAlt: "Illustrative mortgage location drawing sample showing parcel and improvements",
    scope:
      "We draft improvement locations relative to the property linework you supply, add dimensions and ties as instructed, present easements or encroachments per your notes, and format compact sheets suited to lending documentation packages.",
    inputs: [
      "Field measurements of improvements supplied by the client",
      "Property and record information provided for reference",
      "Client sheet standards and symbology",
      "Written instructions on required callouts and tables",
    ],
    deliverables: [
      "Mortgage location drawings with improvement positioning",
      "Dimensioning and ties as instructed",
      "Compact PDF sets alongside editable CAD files",
    ],
    formats: ["DWG", "DXF", "PDF"],
    applications: [
      "Lending and title documentation packages",
      "Closing support drawings prepared by the surveyor",
      "Residential property files and archives",
      "Consistent multi-file production for busy seasons",
    ],
    workflow: [
      { title: "Review inputs", desc: "Measurements and record data are checked for completeness." },
      { title: "Draft improvements", desc: "Structures and features are drawn relative to supplied linework." },
      { title: "Annotate", desc: "Dimensions, ties, and notes are added per your instructions." },
      { title: "Check", desc: "The sheet is reviewed against your standards." },
      { title: "Deliver", desc: "Final files are handed over in the agreed formats." },
    ],
    faqs: [
      {
        q: "Do you determine property lines?",
        a: "No. Drawings are prepared from the data and direction the responsible surveyor supplies; boundary determination and certification remain with them.",
      },
      {
        q: "Can sheets follow our office format?",
        a: "Yes — share your sheet standard or a sample, and the presentation will be matched as closely as the supplied data allows.",
      },
      {
        q: "Can you handle batches of files?",
        a: "Yes, subject to capacity. Volume arrangements are agreed in writing before production starts.",
      },
    ],
    related: ["boundary-survey-drafting", "fema-elevation-certificate-drafting", "as-built-survey-drafting"],
    note: "GeoDrafts provides drafting production only. Boundary determination, review, and certification remain with the licensed surveyor responsible for the project.",
  },
  {
    slug: "subdivision-plat-drafting",
    name: "Subdivision Plat Drafting",
    navLabel: "Subdivision Plat Drafting",
    icon: LayoutGrid,
    short:
      "Multi-lot plat drafting with lot numbering, curve tables, dedications, and sheet indexing, prepared to your office standards.",
    metaTitle: "Subdivision Plat Drafting Services | GeoDrafts",
    metaDescription:
      "Subdivision plat drafting from supplied lot geometry and record data — lot numbering, curve tables, dedications and multi-sheet indexing prepared to your standards.",
    headline: "Multi-Lot Plats, Organized Sheet by Sheet",
    intro:
      "GeoDrafts drafts subdivision plats from your layout geometry and record data — lot linework, curve tables, dedications, and sheet indexing prepared to your office standards.",
    image: IMG.showcase_gis_parcel,
    imageAlt: "Illustrative multi-lot parcel layout sample",
    scope:
      "We draft lot linework from the geometry you supply, build curve and bearing tables as instructed, annotate dedications and easements per your direction, and index multi-sheet plat sets so every sheet cross-references cleanly.",
    inputs: [
      "Lot geometry and coordinate data supplied by the client",
      "Road and right-of-way information",
      "Curve tables and record data provided for reference",
      "Client plat standards and sheet templates",
      "Written direction on dedications and annotations",
    ],
    deliverables: [
      "Plat sheets with lot linework and numbering",
      "Curve and bearing tables as instructed",
      "Multi-sheet indexing and key maps",
      "PDF plots and editable CAD files",
    ],
    formats: ["DWG", "DXF", "PDF"],
    applications: [
      "Development documentation sets",
      "Record plats prepared by the responsible surveyor",
      "Phased development sheet packages",
      "Consistent archiving of subdivision records",
    ],
    workflow: [
      { title: "Review geometry", desc: "Supplied lot data and records are checked and organized." },
      { title: "Draft the plat", desc: "Lot linework, streets, and ROW are drawn to scope." },
      { title: "Tables and notes", desc: "Curve tables, dedications, and notes are added as directed." },
      { title: "Review", desc: "Sheets are checked against the client's checklist." },
      { title: "Deliver", desc: "Indexed sets are handed over in agreed formats." },
    ],
    faqs: [
      {
        q: "Do you create the lot layout?",
        a: "We draft from the layout geometry you supply. Lot design decisions and recordation remain with your licensed professionals.",
      },
      {
        q: "Can you match a specific county presentation style?",
        a: "Yes, share the required format or a sample plat and we will follow it as directed.",
      },
      {
        q: "Can you handle large multi-phase plats?",
        a: "Yes, subject to capacity — multi-sheet sets are indexed and managed consistently across phases.",
      },
    ],
    related: ["boundary-survey-drafting", "alta-nsps-survey-drafting", "mortgage-location-survey-drafting"],
    note: "GeoDrafts provides drafting production only. Plat review, approval, and recordation remain with the responsible licensed surveyor.",
  },
  {
    slug: "easement-survey-drafting",
    name: "Easement Survey Drafting",
    navLabel: "Easement Survey Drafting",
    icon: ScrollText,
    short:
      "Easement exhibits and strip drawings with widths, ties, and descriptive labels, drafted from supplied descriptions and field data.",
    metaTitle: "Easement Survey Drafting Services | GeoDrafts",
    metaDescription:
      "Easement exhibit and strip drawing drafting from supplied descriptions and field data — widths, ties and labels prepared to your standards.",
    headline: "Easement Exhibits With Clear Ties and Widths",
    intro:
      "GeoDrafts prepares easement exhibits and strip drawings from the descriptions and field data you supply — widths, ties, and labels presented exactly as directed.",
    image: IMG.showcase_boundary,
    imageAlt: "Illustrative easement strip drawing sample",
    scope:
      "We draft easement geometry from your descriptions and measurements, place ties to parcel features as instructed, label widths and purposes per your direction, and format exhibits to your standards.",
    inputs: [
      "Easement descriptions supplied by the client",
      "Field ties and measurements",
      "Base boundary or parcel drawings, where available",
      "Client exhibit standards and symbology",
    ],
    deliverables: [
      "Easement exhibit drawings",
      "Strip drawings with widths and ties",
      "Descriptive labels per written direction",
      "PDF plots and editable CAD files",
    ],
    formats: ["DWG", "DXF", "PDF"],
    applications: [
      "Title work support packages",
      "Access and utility easement documentation prepared by the surveyor",
      "Acquisition and review sets",
      "Consistent exhibit archiving",
    ],
    workflow: [
      { title: "Review descriptions", desc: "Supplied descriptions and ties are organized." },
      { title: "Draft geometry", desc: "Easement strips are drawn exactly per the supplied data." },
      { title: "Label", desc: "Widths, ties, and notes are added as directed." },
      { title: "Check", desc: "Exhibits are reviewed against the source direction." },
      { title: "Deliver", desc: "Final files are handed over in the agreed structure." },
    ],
    faqs: [
      {
        q: "Do you write legal descriptions?",
        a: "No. We draft from descriptions and direction the responsible professional supplies; we do not interpret or author legal descriptions.",
      },
      {
        q: "Can exhibits match our standard template?",
        a: "Yes — share your exhibit format and we will follow it.",
      },
      {
        q: "Can you produce multiple exhibits from one base drawing?",
        a: "Yes, multiple strips can be drawn from a common base with consistent presentation.",
      },
    ],
    related: ["boundary-survey-drafting", "route-right-of-way-drafting", "alta-nsps-survey-drafting"],
    note: "GeoDrafts does not interpret legal descriptions or determine rights. All easement determinations and professional sign-off remain with the licensed surveyor.",
  },
  {
    slug: "construction-staking-drafting",
    name: "Construction Staking Drafting",
    navLabel: "Construction Staking Drafting",
    icon: Construction,
    short:
      "Stakeout plans, offset sheets, and coordinate point lists prepared from supplied design and field data, formatted for field crews.",
    metaTitle: "Construction Staking Drafting Services | GeoDrafts",
    metaDescription:
      "Construction staking drafting from supplied design data — stakeout plans, offset sheets and point lists formatted for field crews.",
    headline: "Stakeout Sheets Crews Can Work From",
    intro:
      "GeoDrafts prepares staking plans, offset sheets, and point lists from the design and control data you supply — organized so field crews can set out with confidence.",
    image: IMG.construction_survey,
    imageAlt: "Surveyor with equipment at a construction site",
    scope:
      "We build stakeout plans from supplied design points and alignments, produce offset and cut sheets as instructed, format coordinate point lists to your numbering rules, and organize everything into crew-ready packages.",
    inputs: [
      "Design files and point data supplied by the client",
      "Control information and coordinate references",
      "Client point numbering and sheet standards",
      "Written instructions on required staking detail",
    ],
    deliverables: [
      "Stakeout and setting-out plans",
      "Offset and cut sheets as instructed",
      "Coordinate point lists in agreed formats",
      "PDF plots and editable CAD files",
    ],
    formats: ["DWG", "DXF", "PDF", "CSV point files"],
    applications: [
      "Setting-out support for construction crews",
      "Control documentation packages",
      "Progress check drawings from supplied data",
      "Foundation and layout sheets",
    ],
    workflow: [
      { title: "Review design data", desc: "Supplied points and alignments are organized." },
      { title: "Build staking sheets", desc: "Plans are drafted to your crew's working format." },
      { title: "Point lists", desc: "Coordinates are formatted to your numbering rules." },
      { title: "Check", desc: "Sheets are reviewed against the supplied design." },
      { title: "Deliver", desc: "Crew-ready packages are handed over." },
    ],
    faqs: [
      {
        q: "Do you perform field staking?",
        a: "No. All staking in the field is performed by your crews; we prepare the drawings and point lists they work from.",
      },
      {
        q: "Which point file formats can you deliver?",
        a: "CSV-style point lists in the column structure you specify, alongside DWG/DXF and PDF drawings.",
      },
      {
        q: "Can you work from civil design files?",
        a: "Yes, where design files are supplied, staking information is extracted and formatted as instructed.",
      },
    ],
    related: ["as-built-survey-drafting", "route-right-of-way-drafting", "land-survey-cad-drafting"],
    note: "GeoDrafts provides drafting and data preparation only. Field staking and layout verification remain with the client's survey crews.",
  },
  {
    slug: "utility-survey-mapping",
    name: "Utility Survey and Mapping Drafting",
    navLabel: "Utility Survey and Mapping",
    icon: Cable,
    short:
      "Utility mapping drawings from supplied detection, records, and markout data — structures, line types, and quality-level labelling as instructed.",
    metaTitle: "Utility Survey Mapping & Drafting | GeoDrafts",
    metaDescription:
      "Utility survey mapping and drafting from supplied markout, detection and record data — structure schedules, line types and quality-level labelling prepared to your standards.",
    headline: "Utility Networks Mapped With Clear Quality Levels",
    intro:
      "GeoDrafts drafts utility mapping drawings from the detection records, markout data, and records you supply — structures, line types, and quality-level annotation organized to your standards.",
    image: IMG.showcase_asbuilt,
    imageAlt: "Illustrative utility mapping drawing sample",
    scope:
      "We draw utility networks from supplied markout and detection data, apply your symbology for line types and structures, add quality-level labelling as directed, and organize the mapping so design teams can read conflicts clearly.",
    inputs: [
      "Markout and detection records supplied by the client",
      "Structure data and field observations",
      "Client symbology and quality-level conventions",
      "Base mapping and reference drawings, where available",
    ],
    deliverables: [
      "Utility plan drawings with organized networks",
      "Structure schedules as instructed",
      "Quality-level annotation per your direction",
      "CAD and GIS-ready outputs as agreed",
    ],
    formats: ["DWG", "DXF", "PDF", "Shapefile on request"],
    applications: [
      "Design conflict review support",
      "Asset documentation and records",
      "SUE-style mapping presentation from supplied data",
      "Permit packages prepared by the client",
    ],
    workflow: [
      { title: "Review records", desc: "Markout, detection, and record data are organized." },
      { title: "Draft networks", desc: "Lines and structures are drawn from supplied data." },
      { title: "Label quality levels", desc: "Annotation is applied per your conventions." },
      { title: "Check", desc: "Mapping is reviewed against the source records." },
      { title: "Deliver", desc: "Final files are handed over in the agreed formats." },
    ],
    faqs: [
      {
        q: "Do you perform utility detection?",
        a: "No. Detection and markout are performed by your field teams or locators; we map from the data they supply.",
      },
      {
        q: "Can you follow specific quality-level standards?",
        a: "Yes — labelling follows the conventions and instructions you supply.",
      },
      {
        q: "Can output be delivered as GIS data?",
        a: "Yes, utility mapping can be delivered as structured GIS layers as well as CAD drawings.",
      },
    ],
    related: ["route-right-of-way-drafting", "as-built-survey-drafting", "gis-mapping-services"],
  },
  {
    slug: "route-right-of-way-drafting",
    name: "Route and Right-of-Way Survey Drafting",
    navLabel: "Route and Right-of-Way Drafting",
    icon: Route,
    short:
      "Alignment drawings, strip maps, and right-of-way exhibits for roads, pipelines, and corridors, prepared from supplied stationing and parcel data.",
    metaTitle: "Route & Right-of-Way Survey Drafting | GeoDrafts",
    metaDescription:
      "Route and right-of-way survey drafting from supplied stationing and parcel data — alignment sheets, strip maps and ROW exhibits prepared to your standards.",
    headline: "Linear Projects, Drawn Station by Station",
    intro:
      "GeoDrafts prepares alignment drawings, strip maps, and right-of-way exhibits from the stationing and parcel data you supply — linear documentation organized for roads, pipelines, and corridors.",
    image: IMG.aerial_river,
    imageAlt: "Aerial view of a river corridor suitable for linear route context",
    scope:
      "We draft alignments from supplied stationing data, produce strip maps across the corridor, prepare ROW and take exhibits per your direction, and organize parcel tables and annotation so long linear projects stay consistent.",
    inputs: [
      "Alignment and stationing data supplied by the client",
      "Parcel and ownership information provided for reference",
      "Cross-section or profile data, where supplied",
      "Client standards for sheets, symbols, and tables",
    ],
    deliverables: [
      "Alignment sheets and strip maps",
      "Right-of-way and take exhibits as directed",
      "Parcel tables and annotation as instructed",
      "PDF plots and editable CAD files",
    ],
    formats: ["DWG", "DXF", "PDF", "LandXML on request"],
    applications: [
      "Roadway and pipeline project documentation",
      "Acquisition support packages prepared by the surveyor",
      "Corridor mapping and reference drawings",
      "Consistent multi-sheet linear sets",
    ],
    workflow: [
      { title: "Review stationing", desc: "Alignment and parcel data are organized by station." },
      { title: "Draft alignments", desc: "Corridor linework is drawn to the agreed scope." },
      { title: "Prepare exhibits", desc: "ROW and take exhibits are produced per direction." },
      { title: "Check", desc: "Sheets are reviewed for consistency across the corridor." },
      { title: "Deliver", desc: "Indexed sets are handed over in the agreed formats." },
    ],
    faqs: [
      {
        q: "Do you determine right-of-way limits?",
        a: "No. ROW limits are drafted from the data and direction the responsible surveyor supplies.",
      },
      {
        q: "Can you handle very long corridors?",
        a: "Yes — linear projects are organized into indexed sheet sets, subject to capacity.",
      },
      {
        q: "Can you work with LandXML alignments?",
        a: "Yes, LandXML data can be used as supplied input for alignment drafting.",
      },
    ],
    related: ["easement-survey-drafting", "construction-staking-drafting", "utility-survey-mapping"],
    note: "GeoDrafts provides drafting production only. Right-of-way determination and professional sign-off remain with the responsible licensed surveyor.",
  },
  {
    slug: "drone-point-cloud-drafting",
    name: "Drone Data and Point Cloud Drafting",
    navLabel: "Drone Data and Point Cloud Drafting",
    icon: Plane,
    short:
      "Vector features, contours, and surfaces drafted from supplied orthomosaics, DSMs, and point clouds — processed drone data turned into structured drawings.",
    metaTitle: "Drone Data & Point Cloud to CAD Drafting | GeoDrafts",
    metaDescription:
      "Drone data and point cloud drafting from supplied orthomosaics, DSMs and LAS/LAZ files — vector feature extraction, contours and surfaces prepared to your standards.",
    headline: "From Orthomosaics and Point Clouds to Clean CAD",
    intro:
      "GeoDrafts drafts vector features, contours, and surfaces from the orthomosaics, DSMs, and point clouds you supply — turning processed drone data into organized drawings.",
    image: IMG.terrain_contours,
    imageAlt: "Aerial terrain view with natural contour patterns",
    scope:
      "We extract vector features from supplied orthomosaics and point clouds, draft contours and terrain layers from provided surfaces, organize everything into your layer structure, and deliver CAD or GIS outputs ready for your workflows.",
    inputs: [
      "Orthomosaics (GeoTIFF or similar) supplied by the client",
      "DSM/DTM surface files",
      "LAS/LAZ point clouds",
      "Client symbology, layers, and project instructions",
    ],
    deliverables: [
      "Vector feature extraction drawings",
      "Contour and terrain layers from supplied surfaces",
      "Organized CAD exports with your layering",
      "GIS-ready outputs as agreed",
    ],
    formats: ["DWG", "DXF", "Shapefile", "GeoPackage"],
    applications: [
      "Large-area topographic drafting support",
      "Stockpile and site documentation from supplied data",
      "Corridor and site mapping",
      "Supplementing field survey with drone-derived linework",
    ],
    workflow: [
      { title: "Review data", desc: "Supplied orthomosaics and point clouds are inspected." },
      { title: "Extract features", desc: "Vector linework is drafted from the supplied data." },
      { title: "Build terrain", desc: "Contours and surfaces are organized per your spec." },
      { title: "Check", desc: "Outputs are reviewed against your instructions." },
      { title: "Deliver", desc: "CAD and GIS files are handed over in the agreed structure." },
    ],
    faqs: [
      {
        q: "Do you fly drones or process raw flight data?",
        a: "No. We draft from the processed outputs you supply — orthomosaics, DSMs, and point clouds.",
      },
      {
        q: "Which point cloud formats do you accept?",
        a: "LAS and LAZ as standard; other formats can be discussed per project.",
      },
      {
        q: "Can extraction follow our feature coding?",
        a: "Yes, features are drafted to the codes and symbology you supply.",
      },
    ],
    related: ["topographic-survey-drafting", "gis-mapping-services", "gis-data-conversion"],
  },
  {
    slug: "fema-elevation-certificate-drafting",
    name: "FEMA Elevation Certificate Drafting",
    navLabel: "FEMA Elevation Certificate Drafting",
    icon: FileWarning,
    short:
      "Supporting diagrams and drafted documentation for elevation certificates, prepared from your field data for review by the licensed surveyor.",
    metaTitle: "FEMA Elevation Certificate Drafting Support | GeoDrafts",
    metaDescription:
      "Elevation certificate drafting support — building diagrams and measurement documentation prepared from supplied field data for review and certification by the licensed surveyor.",
    headline: "Elevation Certificate Support, Prepared for Review",
    intro:
      "GeoDrafts prepares supporting diagrams and drafted documentation for elevation certificates from the field data you supply — organized for review and certification by the authorized professional.",
    image: IMG.field_surveyor,
    imageAlt: "Surveyor using a total station instrument in the field",
    scope:
      "We draft building diagrams from supplied measurements, organize elevation documentation per your direction, and format supporting drawings so the certifying professional can review and complete the certificate efficiently.",
    inputs: [
      "Field elevations and building measurements supplied by the client",
      "Photographs and observation records, where provided",
      "Client certificate format and documentation standards",
      "Written instructions on required diagrams",
    ],
    deliverables: [
      "Drafted building diagrams",
      "Measurement and elevation annotation as instructed",
      "Supporting drawing sets in agreed formats",
    ],
    formats: ["PDF", "DWG", "DXF"],
    applications: [
      "Floodplain documentation support packages",
      "Insurance-related drawing sets prepared by the surveyor",
      "Consistent certificate support production",
      "Archiving organized documentation",
    ],
    workflow: [
      { title: "Review field data", desc: "Supplied elevations and measurements are organized." },
      { title: "Draft diagrams", desc: "Building diagrams are drawn from the supplied data." },
      { title: "Annotate", desc: "Measurements and notes are added per your direction." },
      { title: "Check", desc: "Documentation is reviewed against the source data." },
      { title: "Deliver", desc: "Files are handed over for professional review." },
    ],
    faqs: [
      {
        q: "Does GeoDrafts sign elevation certificates?",
        a: "No. Certificates are completed and signed only by authorized licensed professionals; GeoDrafts provides drafting support.",
      },
      {
        q: "What do you need from our field crew?",
        a: "The elevations, measurements, photos, and observations your crew collects, plus your documentation format.",
      },
      {
        q: "Can diagrams follow a specific required layout?",
        a: "Yes — share the required format and we will follow it as directed.",
      },
    ],
    related: ["mortgage-location-survey-drafting", "boundary-survey-drafting", "as-built-survey-drafting"],
    note: "GeoDrafts provides drafting support only. Elevation certificates are completed, reviewed, and signed exclusively by authorized licensed professionals.",
  },
  {
    slug: "cadastral-mapping-services",
    name: "Cadastral Mapping Services",
    navLabel: "Cadastral Mapping",
    icon: BookOpen,
    short:
      "Cadastral mapping and parcel drafting from supplied deeds, plats, and coordinate records — organized parcel maps and land records drawings.",
    metaTitle: "Cadastral Mapping Services & Parcel Drafting | GeoDrafts",
    metaDescription:
      "Cadastral mapping services and parcel drafting from supplied deeds, plats and records — layer-organized cadastral CAD drawings and parcel fabric support for surveying firms.",
    headline: "Cadastral Maps Built From the Record",
    intro:
      "GeoDrafts provides cadastral mapping services from the deeds, recorded plats, and coordinate records you supply — parcel linework, lot and block structures, and land records graphics drafted to your office standards.",
    image: IMG.showcase_gis_parcel,
    imageAlt: "Illustrative cadastral parcel map sample with lot boundaries and record references",
    scope:
      "We draft parcel linework from supplied legal descriptions and recorded plats, organize lot, block, and subdivision structures, annotate dimensions and record references as directed, and assemble cadastral map sheets on your templates. Every drawing follows the written instructions and source records your office provides — interpretation and boundary determination remain with your licensed professionals.",
    inputs: [
      "Deeds, legal descriptions, and recorded plats",
      "Coordinate lists and control information",
      "Existing cadastral drawings or GIS parcel layers",
      "Record references, document numbers, and source notes",
      "Client map templates and cadastral drafting standards",
    ],
    deliverables: [
      "Parcel fabric and cadastral base drawings",
      "Lot, block, and subdivision annotation",
      "Record reference tables and source notes",
      "Map sheets with PDF plots for review",
    ],
    formats: ["DWG", "DXF", "PDF", "SHP/GeoJSON via GIS conversion"],
    applications: [
      "Land records modernization projects",
      "Parcel fabric maintenance drafting",
      "Assessor and mapping department support",
      "Subdivision and annexation record graphics",
    ],
    workflow: [
      { title: "Review records", desc: "Supplied deeds, plats, and references are checked for completeness." },
      { title: "Build the framework", desc: "Parcel structure and control are organized before drafting." },
      { title: "Draft parcels", desc: "Linework and annotation are drawn from the supplied records." },
      { title: "Verify against record", desc: "Geometry and references are checked against the source documents." },
      { title: "Deliver files", desc: "Organized CAD and plot files are handed over in your structure." },
    ],
    faqs: [
      {
        q: "Do you interpret deeds or determine boundaries?",
        a: "No. We draft strictly from the records and the interpretation your licensed surveyor directs — boundary determination always remains with your professionals.",
      },
      {
        q: "Can cadastral work be delivered as GIS layers?",
        a: "Yes. Parcel drawings can be structured for GIS delivery or converted through our GIS data conversion service with attribution as directed.",
      },
      {
        q: "Can you handle jurisdiction-wide record sets?",
        a: "Yes. Batch production of parcel and record graphics is routine — scope, sources, and output standards are agreed in writing before production starts.",
      },
    ],
    related: ["subdivision-plat-drafting", "boundary-survey-drafting", "gis-mapping-services"],
    note: "GeoDrafts provides drafting production only. Records research direction, boundary determination, and certification remain with the licensed professionals responsible for the project.",
  },
  {
    slug: "plot-plan-drafting",
    name: "Plot Plan Drafting",
    navLabel: "Plot Plan Drafting",
    icon: Home,
    short:
      "Plot plan and site plan drafting for permits and documentation — buildings, setbacks, dimensions, and improvements drawn from supplied survey data.",
    metaTitle: "Plot Plan Drafting Services | GeoDrafts",
    metaDescription:
      "Plot plan drafting and site plan drawing services from supplied survey data — buildings, setbacks, easements and improvements drafted to your jurisdiction's requirements.",
    headline: "Plot Plans Ready for Review",
    intro:
      "GeoDrafts drafts plot plans and site plans from your supplied boundary data and site measurements — buildings, setbacks, easements, and improvements laid out clearly for the permit and documentation packages your office prepares.",
    image: IMG.showcase_asbuilt,
    imageAlt: "Illustrative plot plan sample showing buildings, setbacks and dimensions",
    scope:
      "We draw property boundaries from supplied survey data, place buildings and improvements from your measurements, annotate setbacks, dimensions, and easements as directed, and format the sheet to the requirements you supply — including jurisdiction-specific checklists where provided.",
    inputs: [
      "Boundary and control data from your survey",
      "Building footprints and site measurements",
      "Setback and zoning requirements you supply",
      "Jurisdiction checklists or example submissions",
      "Your title block and sheet standards",
    ],
    deliverables: [
      "Plot plan sheets with boundaries and improvements",
      "Setback, dimension, and easement annotation",
      "Existing and proposed conditions layers",
      "Editable CAD files with PDF plots",
    ],
    formats: ["DWG", "DXF", "PDF"],
    applications: [
      "Permit submission packages prepared by the client",
      "Zoning and planning documentation",
      "Property transaction records",
      "Contractor and owner communication",
    ],
    workflow: [
      { title: "Review inputs", desc: "Survey data, measurements, and requirements are checked for completeness." },
      { title: "Set up the sheet", desc: "Your template, scale, and layer standards are applied." },
      { title: "Draft the plan", desc: "Boundaries, buildings, setbacks, and annotation are drawn to scope." },
      { title: "Check requirements", desc: "The sheet is reviewed against the checklist you supplied." },
      { title: "Deliver files", desc: "CAD and PDF files are handed over ready for your submission package." },
    ],
    faqs: [
      {
        q: "Can you follow a specific jurisdiction's plot plan format?",
        a: "Yes. Share the requirements or a past approved example and we will format the sheet accordingly, flagging anything that needs your professional input.",
      },
      {
        q: "Can you show proposed additions or new structures?",
        a: "Yes — proposed improvements are drawn from the design information you supply, on separate layers from existing conditions.",
      },
      {
        q: "Who certifies the plot plan?",
        a: "Where certification is required, your licensed professionals sign. GeoDrafts provides drafting production only.",
      },
    ],
    related: ["mortgage-location-survey-drafting", "as-built-survey-drafting", "boundary-survey-drafting"],
    note: "GeoDrafts provides drafting production only. Survey certification and permit sign-off remain with the licensed professionals responsible for the project.",
  },
  {
    slug: "grading-earthwork-drafting",
    name: "Grading and Earthwork Drafting",
    navLabel: "Grading & Earthwork",
    icon: Layers,
    short:
      "Grading plan drafting, existing-ground surface drawings, and earthwork exhibits — contours, spot elevations, and volume documentation from supplied data.",
    metaTitle: "Grading Plan & Earthwork Drafting Services | GeoDrafts",
    metaDescription:
      "Grading plan drafting, DTM/DEM surface drawings and earthwork volume exhibits — contours, spot elevations and cut-fill documentation drafted from your supplied data.",
    headline: "Grading Plans and Earthwork Exhibits, Drafted Clean",
    intro:
      "GeoDrafts prepares grading plan drafting and earthwork documentation from your supplied survey data and design direction — existing-ground contours, proposed grades, spot elevation plans, and volume exhibits drawn to your standards.",
    image: IMG.terrain_contours,
    imageAlt: "Illustrative grading plan sample with contours and spot elevations",
    scope:
      "We draft existing-ground contour plans from supplied point and breakline data, prepare DTM/DEM surface drawings, draw proposed grading linework from your design information, produce spot elevation plans, and assemble cut-fill and earthwork exhibits from the calculations your office supplies.",
    inputs: [
      "Point files and breakline data from your survey",
      "Design grades, pads, and slope instructions",
      "Earthwork calculations and quantity tables you supply",
      "Drainage and structure information as required",
      "Client templates, layer standards, and surface styles",
    ],
    deliverables: [
      "Existing-ground contour and DTM/DEM drawings",
      "Proposed grading plans with spot elevations",
      "Cut-fill and volume exhibit sheets",
      "Organized CAD files with PDF plots",
    ],
    formats: ["DWG", "DXF", "PDF", "LandXML on request"],
    applications: [
      "Site design documentation support",
      "Earthwork bid and quantity exhibits",
      "Drainage and stormwater review graphics",
      "Construction grading documentation",
    ],
    workflow: [
      { title: "Review terrain data", desc: "Supplied points, breaklines, and design information are checked." },
      { title: "Build the base", desc: "Existing-ground surfaces and contours are drawn from the data." },
      { title: "Draft proposed grades", desc: "Design linework and spot elevations are added per your direction." },
      { title: "Assemble exhibits", desc: "Volume and cut-fill sheets are prepared from your calculations." },
      { title: "Deliver files", desc: "Checked drawings are handed over in the agreed structure." },
    ],
    faqs: [
      {
        q: "Do you calculate earthwork volumes?",
        a: "Volume computations remain with your engineers. We draft the exhibits and organize the quantity tables your office supplies, presented exactly as directed.",
      },
      {
        q: "Can you work in Civil 3D templates?",
        a: "Yes. We draft on the templates and styles you supply, including Civil 3D survey drafting environments with your surface and label styles.",
      },
      {
        q: "How do you handle design changes mid-project?",
        a: "Revised design information is incorporated as directed, with versioned files so your office can track what changed between issues.",
      },
    ],
    related: ["topographic-survey-drafting", "road-highway-survey-drafting", "construction-staking-drafting"],
    note: "GeoDrafts provides drafting production only. Grading design, engineering judgment, and certification remain with the licensed professionals responsible for the project.",
  },
  {
    slug: "road-highway-survey-drafting",
    name: "Road and Highway Survey Drafting",
    navLabel: "Road & Highway Survey",
    icon: Milestone,
    short:
      "Road and highway survey CAD drafting — alignments, plan and profile sheets, cross-sections, and corridor mapping from supplied survey and design data.",
    metaTitle: "Road & Highway Survey CAD Drafting | GeoDrafts",
    metaDescription:
      "Road and highway survey CAD drafting — alignment plans, plan-and-profile sheets, cross-section sets and corridor base mapping drafted from supplied survey data.",
    headline: "Corridor Sheets That Stay Consistent, Mile After Mile",
    intro:
      "GeoDrafts provides road and highway survey CAD drafting from your supplied alignment geometry and corridor data — stationing, plan and profile sheets, cross-section sets, and existing-features mapping produced to your sheet standards.",
    image: IMG.construction_survey,
    imageAlt: "Illustrative road corridor survey plan sample with alignment and stationing",
    scope:
      "We draft centerline alignments and stationing from supplied geometry, assemble plan-and-profile sheets, plot cross-sections from supplied section data, and map existing corridor features — pavement edges, structures, drainage, and utilities — on your templates with consistent sheet series production.",
    inputs: [
      "Alignment geometry and stationing information",
      "Profile and cross-section data from your survey",
      "Corridor topographic and utility information",
      "Design direction and instructed annotation",
      "Sheet series templates and plan-production standards",
    ],
    deliverables: [
      "Alignment and stationing plan sheets",
      "Plan-and-profile sheet series",
      "Cross-section drawing sets",
      "Corridor existing-conditions base mapping",
    ],
    formats: ["DWG", "DXF", "DGN on request", "PDF"],
    applications: [
      "Highway and municipal road documentation support",
      "Corridor as-built and record drawings",
      "Right-of-way and alignment exhibits",
      "Construction plan production support",
    ],
    workflow: [
      { title: "Review corridor data", desc: "Alignment, section, and topo data are checked for completeness." },
      { title: "Set up the series", desc: "Sheet templates, matchlines, and stationing are established." },
      { title: "Draft the sheets", desc: "Plan, profile, and section sheets are produced to scope." },
      { title: "Check consistency", desc: "Stationing, annotation, and sheet-to-sheet continuity are verified." },
      { title: "Deliver the set", desc: "The organized sheet series is handed over for professional review." },
    ],
    faqs: [
      {
        q: "Do you produce DGN files for DOT workflows?",
        a: "Yes — DGN output on MicroStation-based standards is available on request, alongside DWG and DXF delivery.",
      },
      {
        q: "Can you handle long corridors with many sheets?",
        a: "Yes. Sheet-series production with consistent stationing, matchlines, and annotation is a routine part of corridor drafting.",
      },
      {
        q: "Do you make alignment or design decisions?",
        a: "No. Geometry is drafted exactly from the data and direction your engineers and surveyors supply.",
      },
    ],
    related: ["route-right-of-way-drafting", "construction-staking-drafting", "grading-earthwork-drafting"],
    note: "GeoDrafts provides drafting production only. Engineering design and survey certification remain with the licensed professionals responsible for the project.",
  },
  {
    slug: "pdf-to-cad-conversion",
    name: "PDF to CAD Conversion",
    navLabel: "PDF to CAD Conversion",
    icon: FileDigit,
    short:
      "PDF, paper, and raster survey plans converted into structured CAD — PDF to DWG redrafting and raster-to-vector conversion from the documents you supply.",
    metaTitle: "PDF to CAD Conversion & PDF to DWG Services | GeoDrafts",
    metaDescription:
      "PDF to CAD conversion and PDF to DWG drafting services — paper map to CAD, raster to vector conversion and survey plan digitization, redrawn to your layer standards.",
    headline: "From Scanned Plan to Structured CAD",
    intro:
      "GeoDrafts provides PDF to CAD conversion for surveyors and engineers — legacy scans, PDFs, and paper maps redrawn into organized, editable DWG files with your layer structure, symbols, and annotation standards.",
    image: IMG.showcase_conversion,
    imageAlt: "Illustrative PDF to CAD conversion sample showing redrawn survey linework",
    scope:
      "We redraw supplied PDFs and scans as true CAD geometry: linework vectorized to stated dimensions, text and symbols recreated to your standards, layers organized per your conventions, and title blocks rebuilt on your templates. Anything illegible in the source is flagged in writing rather than guessed.",
    inputs: [
      "PDF files, scans, or photographed paper plans",
      "Scale references and stated dimensions",
      "Layer standards and output templates",
      "Symbol libraries or sample drawings",
      "Priority and scope instructions for batch work",
    ],
    deliverables: [
      "Redrawn DWG/DXF files with organized layers",
      "Recreated annotation, symbols, and title blocks",
      "Overlay comparison plots against the source",
      "Batch delivery logs for archive projects",
    ],
    formats: ["DWG", "DXF", "PDF"],
    applications: [
      "Legacy plan archive digitization",
      "Record drawing conversion for reuse",
      "Survey sketch and field book digitization",
      "Raster to vector conversion for GIS preparation",
    ],
    workflow: [
      { title: "Assess the source", desc: "Scans are reviewed for legibility, scale references, and scope." },
      { title: "Set standards", desc: "Your layers, symbols, and template are configured for the conversion." },
      { title: "Redraw the plan", desc: "Geometry is vectorized and annotation recreated to stated dimensions." },
      { title: "Compare and check", desc: "The redraw is overlaid against the source and checked line by line." },
      { title: "Deliver files", desc: "Structured CAD files are handed over with conversion notes." },
    ],
    faqs: [
      {
        q: "How accurate is a converted drawing?",
        a: "Geometry is drawn to the dimensions stated on the source document. Where a scan lacks scale information, we confirm the approach with you before drafting.",
      },
      {
        q: "What if parts of the scan are illegible?",
        a: "We flag them in writing and await your direction — converted drawings never contain guessed content.",
      },
      {
        q: "Can you convert entire plan archives in bulk?",
        a: "Yes. Batch archive conversions are quoted against volume, with agreed naming, layering, and delivery structure across the set.",
      },
    ],
    related: ["gis-data-conversion", "point-cloud-to-cad-conversion", "land-survey-cad-drafting"],
    note: "GeoDrafts provides conversion drafting only. Source documents remain the governing record; converted files are prepared for production use under your professional review.",
  },
  {
    slug: "point-cloud-to-cad-conversion",
    name: "Point Cloud to CAD Conversion",
    navLabel: "Point Cloud to CAD",
    icon: ScanLine,
    short:
      "Point cloud to CAD conversion and scan-to-CAD drafting — LiDAR and laser scan datasets turned into clean, layer-organized CAD linework.",
    metaTitle: "Point Cloud to CAD Conversion Services | GeoDrafts",
    metaDescription:
      "Point cloud to CAD conversion and scan-to-CAD drafting — LiDAR survey data processing into planimetric drawings, sections and contours from your supplied datasets.",
    headline: "Point Clouds In, Drafted Plans Out",
    intro:
      "GeoDrafts converts supplied point cloud and LiDAR data into clean CAD deliverables — planimetric linework, building sections and elevations, and contour drawings traced from the datasets your crews capture.",
    image: IMG.aerial_river,
    imageAlt: "Illustrative point cloud to CAD conversion sample with traced planimetric linework",
    scope:
      "We extract planimetric features as directed from supplied LAS/LAZ/E57 datasets, draft building elevations and sections from laser scans, generate contour linework from point data, and organize every output on your layers — working from decimated exports or agreed tiling for large datasets.",
    inputs: [
      "Point cloud files (LAS, LAZ, E57, or decimated exports)",
      "Intensity imagery and scan positions where available",
      "Feature lists defining what to trace",
      "Drafting instructions and level-of-detail direction",
      "Client templates and layer standards",
    ],
    deliverables: [
      "Planimetric CAD drawings traced from the cloud",
      "Building sections and elevation drawings",
      "Contour and terrain linework",
      "Layer-organized files with PDF review plots",
    ],
    formats: ["DWG", "DXF", "PDF", "Source: LAS/LAZ/E57/RCS"],
    applications: [
      "Scan-to-CAD existing conditions documentation",
      "Mobile mapping and LiDAR corridor extraction",
      "As-built and heritage documentation support",
      "Drone LiDAR data processing for survey deliverables",
    ],
    workflow: [
      { title: "Review the dataset", desc: "Coverage, density, and coordinate information are confirmed." },
      { title: "Agree the feature list", desc: "What gets traced — and to what detail — is fixed in writing." },
      { title: "Trace and draft", desc: "Features are extracted and drafted to your standards." },
      { title: "Verify coverage", desc: "Drafted work is checked against the cloud for completeness." },
      { title: "Deliver files", desc: "Organized CAD files are handed over with processing notes." },
    ],
    faqs: [
      {
        q: "Our datasets are very large — how do we send them?",
        a: "Decimated exports or agreed tiled deliveries work well. Transfer method and data volume are settled before the project starts.",
      },
      {
        q: "Which features do you extract?",
        a: "Exactly what your feature list defines — from full planimetric capture to specific structures, edges, or levels.",
      },
      {
        q: "Do you produce 3D models?",
        a: "Our core output is 2D plans, sections, and elevations; 3D linework extraction is available on request where the dataset supports it.",
      },
    ],
    related: ["drone-point-cloud-drafting", "topographic-survey-drafting", "as-built-survey-drafting"],
    note: "GeoDrafts provides conversion drafting only. Registration, control, and survey certification remain with the licensed professionals responsible for the dataset.",
  },
  {
    slug: "survey-data-processing",
    name: "Survey Data Processing",
    navLabel: "Survey Data Processing",
    icon: Cpu,
    short:
      "Survey data processing services — point files organized, coordinate lists checked, and total station or GNSS field data prepared drafting-ready.",
    metaTitle: "Survey Data Processing Services | GeoDrafts",
    metaDescription:
      "Survey data processing services — point file normalization, coordinate list checks, total station and GNSS data conversion into drafting-ready structures.",
    headline: "Field Data, Processed and Ready to Draft",
    intro:
      "GeoDrafts provides survey data processing services that turn raw field output into organized, drafting-ready structures — point files normalized, coordinate lists checked against supplied control, and coding mapped to your office standards.",
    image: IMG.gnss_receiver,
    imageAlt: "Illustrative survey data processing sample with organized point files",
    scope:
      "We clean and normalize point files (CSV, TXT, PNEZD-style lists), check coordinate listings against the control information you supply, convert data between common total station and GNSS formats, map field codes to your layer structure, and prepare organized base files that drafting can start from immediately.",
    inputs: [
      "Raw point files from total station or GNSS crews",
      "Field books, sketches, and coding notes",
      "Control and coordinate reference information",
      "Your coding standards and layer mapping",
      "Target formats and folder structure",
    ],
    deliverables: [
      "Normalized, consistently coded point files",
      "Organized coordinate listings",
      "Exception reports flagging gaps or conflicts",
      "Drafting-ready base files in your structure",
    ],
    formats: ["CSV", "TXT", "DWG", "DXF", "LandXML on request"],
    applications: [
      "Overflow processing during busy field seasons",
      "Legacy survey data normalization",
      "Pre-drafting quality checks",
      "Office data standardization programs",
    ],
    workflow: [
      { title: "Review the data", desc: "Files, formats, and control references are checked on arrival." },
      { title: "Normalize", desc: "Point codes, coordinates, and structure are aligned to your standards." },
      { title: "Check", desc: "Listings are verified against supplied control and field notes." },
      { title: "Flag exceptions", desc: "Gaps and conflicts are reported in writing for your direction." },
      { title: "Deliver", desc: "Clean, organized data is handed over ready for drafting." },
    ],
    faqs: [
      {
        q: "Do you run adjustments or least-squares analysis?",
        a: "No. Network adjustment and survey computation remain with your office — we organize, convert, and prepare the data around that professional work.",
      },
      {
        q: "Which instrument formats can you handle?",
        a: "Common total station and GNSS export formats are fine. Share a sample file and we will confirm handling before production starts.",
      },
      {
        q: "What happens when data is missing or inconsistent?",
        a: "It is flagged in the exception report with file and point references — we never fill gaps by assumption.",
      },
    ],
    related: ["land-survey-cad-drafting", "pdf-to-cad-conversion", "point-cloud-to-cad-conversion"],
    note: "GeoDrafts provides data preparation only. Survey computation, adjustment, and certification remain with the licensed professionals responsible for the project.",
  },
];

// Alternate search terms per service — rendered on each service page for keyword coverage
export const SERVICE_KEYWORDS = {
  "land-survey-cad-drafting": ["survey CAD drafting services", "land surveying drafting services", "CAD drafting for surveyors", "professional survey drafting company", "outsourced survey CAD services", "land survey drafting outsourcing"],
  "topographic-survey-drafting": ["topographic survey CAD services", "contour mapping and drafting", "elevation survey drawings", "existing ground level plans", "spot level mapping", "topo drafting services"],
  "boundary-survey-drafting": ["property line survey drawings", "boundary survey CAD drafting", "property survey drafting", "boundary retracement drafting", "lot line survey drawings"],
  "alta-nsps-survey-drafting": ["ALTA survey CAD drafting services", "ALTA/NSPS land title survey drafting", "commercial title survey drafting", "ALTA Table A drafting", "title survey production support"],
  "as-built-survey-drafting": ["as-built CAD drawings", "existing conditions drafting", "record drawing drafting", "construction as-built drawings", "post-construction documentation"],
  "gis-mapping-services": ["geospatial CAD services", "parcel mapping services", "spatial data analysis", "geospatial database creation", "GIS map production"],
  "gis-data-conversion": ["CAD to GIS conversion", "shapefile to CAD conversion", "georeferencing and map digitization", "GIS digitization services", "geospatial data processing"],
  "mortgage-location-survey-drafting": ["mortgage inspection drawings", "title survey drafting support", "lending survey drawings", "mortgage survey production"],
  "subdivision-plat-drafting": ["subdivision survey drawings", "final plat drafting", "plat map drafting services", "lot and block plat drafting", "record plat production"],
  "easement-survey-drafting": ["easement and right-of-way mapping", "utility easement exhibits", "access easement drawings", "easement exhibit drafting"],
  "construction-staking-drafting": ["setting-out survey drawings", "construction survey CAD drafting", "site layout plan drafting", "stakeout coordinate sheets", "building footprint mapping"],
  "utility-survey-mapping": ["underground utility mapping", "utility survey CAD drafting", "water and sewer survey plans", "stormwater drainage mapping", "as-built utility drawings"],
  "route-right-of-way-drafting": ["right-of-way mapping services", "route survey drafting", "ROW exhibit drafting", "corridor survey drawings"],
  "drone-point-cloud-drafting": ["drone survey CAD drafting", "UAV mapping services", "photogrammetry CAD services", "orthomosaic mapping services", "aerial survey mapping"],
  "fema-elevation-certificate-drafting": ["FEMA EC documentation support", "flood zone survey drafting", "LOMA documentation graphics", "elevation certificate drawings"],
  "cadastral-mapping-services": ["parcel fabric drafting", "land records mapping", "cadastral survey drawings", "cadastral parcel mapping"],
  "plot-plan-drafting": ["site plan drafting services", "permit plot plans", "residential site plans", "plot plan drawing services"],
  "grading-earthwork-drafting": ["grading plan drafting", "earthwork and volume calculations", "DTM drafting", "DEM mapping", "cut and fill exhibits"],
  "road-highway-survey-drafting": ["road and highway survey CAD", "highway alignment drawings", "plan and profile drafting", "cross section drafting"],
  "pdf-to-cad-conversion": ["PDF to DWG conversion", "PDF to CAD drafting services", "paper map to CAD conversion", "raster to vector conversion", "survey sketch digitization"],
  "point-cloud-to-cad-conversion": ["scan-to-CAD drafting services", "LiDAR survey data processing", "point cloud conversion company", "laser scan to CAD"],
  "survey-data-processing": ["total station data processing", "GNSS survey data processing", "point file to CAD conversion", "GPS survey data conversion"],
};

export const getService = (slug) => SERVICES.find((s) => s.slug === slug);

export const CAD_LAYER_BY_SERVICE = {
  "land-survey-cad-drafting": "boundary",
  "topographic-survey-drafting": "contours",
  "boundary-survey-drafting": "boundary",
  "alta-nsps-survey-drafting": "text",
  "as-built-survey-drafting": "structures",
  "gis-mapping-services": "parcels",
  "gis-data-conversion": "parcels",
  "mortgage-location-survey-drafting": "improvements",
  "subdivision-plat-drafting": "boundary",
  "easement-survey-drafting": "boundary",
  "construction-staking-drafting": "spots",
  "utility-survey-mapping": "utilities",
  "route-right-of-way-drafting": "road",
  "drone-point-cloud-drafting": "contours",
  "fema-elevation-certificate-drafting": "spots",
  "cadastral-mapping-services": "boundary",
  "plot-plan-drafting": "improvements",
  "grading-earthwork-drafting": "contours",
  "road-highway-survey-drafting": "road",
  "pdf-to-cad-conversion": "text",
  "point-cloud-to-cad-conversion": "spots",
  "survey-data-processing": "spots",
};

export const LAYER_SERVICE = {
  boundary: "boundary-survey-drafting",
  contours: "topographic-survey-drafting",
  structures: "as-built-survey-drafting",
  improvements: "mortgage-location-survey-drafting",
  road: "route-right-of-way-drafting",
  utilities: "utility-survey-mapping",
  spots: "fema-elevation-certificate-drafting",
  text: "alta-nsps-survey-drafting",
  parcels: "gis-mapping-services",
};

export const getCadLayer = (slug) => CAD_LAYER_BY_SERVICE[slug] || "boundary";

export const SERVICE_PLAN = {
  "land-survey-cad-drafting": { focus: "boundary", on: {} },
  "topographic-survey-drafting": { focus: "contours", on: { structures: false, improvements: false, road: false } },
  "boundary-survey-drafting": { focus: "boundary", on: { contours: false, structures: false, improvements: false, road: false, spots: false } },
  "alta-nsps-survey-drafting": { focus: "text", on: {} },
  "as-built-survey-drafting": { focus: "structures", on: { contours: false, spots: false } },
  "gis-mapping-services": { focus: "parcels", on: { parcels: true, contours: false, structures: false, improvements: false, road: false, spots: false } },
  "gis-data-conversion": { focus: "parcels", on: { parcels: true, structures: false, improvements: false, road: false, spots: false } },
  "mortgage-location-survey-drafting": { focus: "improvements", on: { contours: false, spots: false } },
  "subdivision-plat-drafting": { focus: "boundary", on: { parcels: true, contours: false, structures: false, improvements: false, road: false, spots: false } },
  "easement-survey-drafting": { focus: "boundary", on: { contours: false, structures: false, improvements: false, spots: false } },
  "construction-staking-drafting": { focus: "spots", on: { contours: false, improvements: false } },
  "utility-survey-mapping": { focus: "utilities", on: { utilities: true, contours: false, spots: false } },
  "route-right-of-way-drafting": { focus: "road", on: { contours: false, structures: false, improvements: false, spots: false } },
  "drone-point-cloud-drafting": { focus: "contours", on: { structures: false, improvements: false, road: false } },
  "fema-elevation-certificate-drafting": { focus: "spots", on: { contours: false, improvements: false, road: false } },
  "cadastral-mapping-services": { focus: "boundary", on: { parcels: true, contours: false, structures: false, improvements: false, road: false, spots: false } },
  "plot-plan-drafting": { focus: "improvements", on: { contours: false, spots: false } },
  "grading-earthwork-drafting": { focus: "contours", on: { structures: false, improvements: false, road: false } },
  "road-highway-survey-drafting": { focus: "road", on: { contours: false, structures: false, improvements: false, spots: false } },
  "pdf-to-cad-conversion": { focus: "text", on: { contours: false, spots: false, utilities: false } },
  "point-cloud-to-cad-conversion": { focus: "spots", on: { structures: false, improvements: false, road: false } },
  "survey-data-processing": { focus: "text", on: { contours: false, structures: false, improvements: false, road: false } },
};

export const PORTFOLIO = [
  { title: "Topographic Survey Drawing", tag: "CAD Drafting", image: IMG.showcase_topo, alt: "Illustrative topographic survey drawing sample" },
  { title: "Property Boundary Plan", tag: "CAD Drafting", image: IMG.showcase_boundary, alt: "Illustrative property boundary plan sample" },
  { title: "Contour & Elevation Map", tag: "Terrain", image: IMG.showcase_contour, alt: "Illustrative contour and elevation map sample" },
  { title: "Existing Conditions Plan", tag: "CAD Drafting", image: IMG.showcase_asbuilt, alt: "Illustrative existing conditions plan sample" },
  { title: "GIS Parcel Map", tag: "GIS", image: IMG.showcase_gis_parcel, alt: "Illustrative GIS parcel map sample" },
  { title: "CAD-to-GIS Conversion Workflow", tag: "GIS", image: IMG.showcase_conversion, alt: "Illustrative CAD-to-GIS conversion workflow sample" },
];

export const AUDIENCES = [
  {
    title: "Land Surveying Companies",
    desc: "Supplementary drafting production for surveying firms — drawings prepared from your field data, to your standards, during busy seasons and beyond.",
  },
  {
    title: "Civil and Land Development Consultants",
    desc: "Base plans, topographic drafting, and existing-conditions drawings prepared from supplied data to support design and documentation workflows.",
  },
  {
    title: "GIS and Geospatial Teams",
    desc: "Data conversion, digitization, layer organization, and map production that keeps spatial datasets structured and ready for use.",
  },
  {
    title: "Engineering and Infrastructure Consultants",
    desc: "Organized site documentation and spatial deliverables prepared from supplied measurements and records for project teams.",
  },
];
